/**
 * The just-in-time "how would new money be invested?" question — ONE card, rendered identically by
 * every module's suggestion engine.
 *
 * The answer is an INPUT to the modelling (invest strategies price at it), so each module shows
 * this BEFORE its engine runs, not as a disclosure after a ranking that would reshuffle on the
 * answer. The caller owns the gating condition (only when the run would actually invest money,
 * only while unanswered) and the two exits:
 *   - onAnswer(profile): save to the client record (goals.newMoneyInvestmentProfile) — asked at
 *     most once, ever, across every module;
 *   - onSkip(): run at Conservative WITHOUT recording an answer, so the question re-offers next
 *     time. "Chose Conservative" and "never answered" are different provenance.
 */
import React from 'react';
import { Button } from './ui/index.tsx';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS } from '../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../shared/src/calculation/investmentProfiles.ts';

export interface NewMoneyProfileGateProps {
    onAnswer: (profile: InvestmentProfileId) => void;
    onSkip: () => void;
    /** One sentence naming what this module's strategies free money up FROM (consumer voice). */
    contextLine?: string;
}

const NewMoneyProfileGate = ({ onAnswer, onSkip, contextLine }: NewMoneyProfileGateProps) => (
    <div className="mt-3 rounded-xl bg-white border border-slate-200/70 border-l-4 border-l-blue-500 p-4 shadow-sm">
        <p className="text-sm font-semibold text-gray-900">One quick question before I model your options</p>
        <p className="text-sm text-slate-700 mt-1.5">
            {contextLine || 'Some of the strategies I’ll compare invest money that gets freed up — for example after selling your home.'}{' '}
            <strong>How would that money be invested?</strong> Pick the approach that feels right; if you&apos;re not sure,
            skip and I&apos;ll assume Conservative, the most cautious option.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
            {INVESTMENT_PROFILE_IDS.map((id) => (
                <button
                    key={id}
                    type="button"
                    onClick={() => onAnswer(id)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:border-blue-500 hover:text-blue-700 transition-colors"
                >
                    {INVESTMENT_PROFILES[id].label}
                </button>
            ))}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500">Your choice is saved to the client profile, so you&apos;ll only be asked once &mdash; and you can change it there any time.</p>
            <Button variant="secondary" size="sm" onClick={onSkip}>
                Skip &mdash; assume Conservative for now
            </Button>
        </div>
    </div>
);

export default NewMoneyProfileGate;
